import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listUnknownAtsHosts, type UnknownAtsHostRoll } from '../api/unknown-ats';
import { formatDate } from '../lib/format';

interface HostRowProps {
  host: UnknownAtsHostRoll;
}

function HostRow({ host }: HostRowProps): React.ReactElement {
  const latestError = host.sampleErrors[host.sampleErrors.length - 1];

  return (
    <tr>
      <td className="px-4 py-3 font-mono text-xs text-text-primary">{host.host}</td>
      <td className="px-4 py-3 text-right font-semibold tabular-nums">{host.count.toLocaleString()}</td>
      <td className="px-4 py-3 text-text-muted text-xs">{formatDate(host.firstSeen)}</td>
      <td className="px-4 py-3 text-text-muted text-xs">{formatDate(host.lastSeen)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {host.sampleRunIds.map((runId) => (
            <Link
              key={runId}
              to={`/runs/${runId}`}
              className="inline-block px-1.5 py-0.5 rounded bg-surface-subtle border border-border font-mono text-xs text-brand-700 hover:bg-brand-50"
            >
              {runId.slice(0, 8)}
            </Link>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-xs">
        {latestError ? (
          <details>
            <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
              #{latestError.eventSeq} @ {formatDate(latestError.capturedAt)}
            </summary>
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[10px] bg-surface-subtle border border-border rounded p-2 max-w-xs">
              {JSON.stringify(latestError.payload, null, 2)}
            </pre>
          </details>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
    </tr>
  );
}

export function UnknownAtsHosts(): React.ReactElement {
  const [cursor, setCursor] = useState<string | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['unknown-ats-hosts', { cursor }],
    queryFn: () => listUnknownAtsHosts({ cursor, limit: 50 }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Unknown ATS Hosts</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Hosts seen during external-apply where the ATS was not recognised. Sorted by hit count — highest first.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle border-b border-border">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Host</th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wide">Count</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">First Seen</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Last Seen</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Sample Runs</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Latest Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((host) => (
              <HostRow key={host.host} host={host} />
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                  No unknown-ATS hits recorded yet. When the worker encounters an unrecognised ATS, it will appear here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.nextCursor && (
        <button
          onClick={() => setCursor(data.nextCursor ?? undefined)}
          className="text-sm text-brand-600 hover:underline"
        >
          Next page →
        </button>
      )}
    </div>
  );
}
